import { useState, useEffect } from 'react';
import SettingsModal from '../../../components/ui/SettingsModal/SettingsModal';
import { LoadingState } from '../../../components/ui/LoadingState';
import FormModal from '../../../components/ui/FormModal/FormModal';
import StatusPill from '../../../components/ui/StatusPill/StatusPill';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { Icon } from '../../../components/ui/Icon';
import { Button } from '../../../components/ui/Button2';
import { Input } from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Switch } from '../../../components/ui/Switch';
import { Avatar, AvatarFallback } from '../../../components/ui/Avatar';
import { useConfirm } from '../../../components/ui/AlertDialog';
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
} from '../../../components/ui/InputGroup';
import { workspaceService } from '../../../services/workspaceService';
import VulnerabilityManagementService from '../../../services/VulnerabilityManagementService';
import styles from './WorkspaceSettingsModal.module.css';

const USER_STATUS_PRESET = {
  active:   { tone: 'success', label: 'Active' },
  inactive: { tone: 'neutral', label: 'Inactive' },
};

const userStatusPreset = (status) =>
  USER_STATUS_PRESET[status] ?? { tone: 'warning', label: 'Pending' };

// The Add/Edit Role FormModal emits one checkbox-group field per permission
// category (label = category name) so the grouped layout is preserved. Field
// names are namespaced to avoid colliding with role_name / description.
const PERM_FIELD_PREFIX = 'permcat:';

const SECTIONS = [
  { id: 'overview',      label: 'Overview',            icon: <Icon name="overview" size={18} />,     group: 'Overview' },
  { id: 'users',         label: 'User Management',     icon: <Icon name="users" size={18} />,        group: 'Management' },
  { id: 'roles',         label: 'Roles & Permissions', icon: <Icon name="star" size={18} />,         group: 'Management' },
  { id: 'notifications', label: 'Notifications',       icon: <Icon name="notification" size={18} />, group: 'Settings' },
];

const WorkspaceSettingsModal = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState('overview');

  // User Management State
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Role Management State
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState([]);

  // Notification Settings State
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Vulnerability Notification Preferences State
  const [vulnPrefs, setVulnPrefs] = useState({
    digest_enabled: true,
    digest_interval_hours: 168,
    realtime_enabled: true,
    active_only: true
  });
  const [savedVulnPrefs, setSavedVulnPrefs] = useState(null);
  const [savingVulnPrefs, setSavingVulnPrefs] = useState(false);
  const [vulnPrefsError, setVulnPrefsError] = useState('');
  const vulnPrefsChanged = savedVulnPrefs !== null
    && JSON.stringify(vulnPrefs) !== JSON.stringify(savedVulnPrefs);

  // UI State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const { confirm, confirmDialog } = useConfirm();

  // Load initial data
  useEffect(() => {
    loadWorkspaceData();
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (searchQuery) {
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  // Load all workspace data
  const loadWorkspaceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [usersRes, rolesRes, permissionsRes, notificationsRes, vulnPrefsRes] = await Promise.all([
        workspaceService.getUsers(),
        workspaceService.getRoles(),
        workspaceService.getPermissions(),
        workspaceService.getNotificationSettings(),
        VulnerabilityManagementService.getNotificationPreferences()
      ]);

      setUsers(usersRes.users || []);
      setFilteredUsers(usersRes.users || []);
      setRoles(rolesRes.roles || []);
      setPermissions(permissionsRes.categories || []);
      setNotificationEmails(notificationsRes.notification_emails || []);
      if (vulnPrefsRes) {
        setVulnPrefs(vulnPrefsRes);
        setSavedVulnPrefs(vulnPrefsRes);
      }

      // Select first role by default
      if (rolesRes.roles && rolesRes.roles.length > 0) {
        const defaultRole = rolesRes.roles.find(r => r.role_id === 'role_admin') || rolesRes.roles[0];
        setSelectedRole(defaultRole);
        setSelectedRolePermissions(defaultRole.permissions || []);
      }
    } catch (err) {
      console.error('Error loading workspace data:', err);
      setError('Failed to load workspace data');
    } finally {
      setLoading(false);
    }
  };

  // User Management Handlers
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowAddUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowAddUserModal(true);
  };

  const handleSaveUser = async (userData) => {
    try {
      setLoadingUsers(true);
      if (editingUser) {
        await workspaceService.updateUser(editingUser.user_id, userData);
      } else {
        await workspaceService.inviteUser(userData);
      }
      await loadWorkspaceData();
      setShowAddUserModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error('Error saving user:', err);
      alert(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find((x) => x.user_id === userId);
    const ok = await confirm({
      title: 'Remove user?',
      description: `Remove ${user?.name || user?.email || 'this user'} from the workspace?`,
      confirmLabel: 'Remove',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      setLoadingUsers(true);
      await workspaceService.deleteUser(userId);
      await loadWorkspaceData();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      setLoadingUsers(true);
      await workspaceService.deactivateUser(userId);
      await loadWorkspaceData();
    } catch (err) {
      console.error('Error deactivating user:', err);
      alert(err.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Role Management Handlers
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setSelectedRolePermissions(role.permissions || []);
  };

  const handleAddRole = () => {
    setEditingRole(null);
    setShowAddRoleModal(true);
  };

  const handleEditRole = (role) => {
    if (role.is_system_role) {
      alert('System roles cannot be modified');
      return;
    }
    setEditingRole(role);
    setShowAddRoleModal(true);
  };

  const closeRoleModal = () => {
    setShowAddRoleModal(false);
    setEditingRole(null);
  };

  // FormModal calls onClose() on success and surfaces a thrown error in its
  // own footer, so we rethrow here instead of alerting + closing manually.
  const handleSaveRole = async (roleData) => {
    try {
      if (editingRole) {
        await workspaceService.updateRole(editingRole.role_id, roleData);
      } else {
        await workspaceService.createRole(roleData);
      }
      await loadWorkspaceData();
    } catch (err) {
      console.error('Error saving role:', err);
      throw new Error(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    const role = roles.find(r => r.role_id === roleId);
    if (role?.is_system_role) {
      alert('System roles cannot be deleted');
      return;
    }
    if (role?.user_count > 0) {
      alert(`Cannot delete role with ${role.user_count} assigned users`);
      return;
    }
    const ok = await confirm({
      title: 'Delete role?',
      description: `Delete the "${role?.role_name}" role? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await workspaceService.deleteRole(roleId);
      await loadWorkspaceData();
    } catch (err) {
      console.error('Error deleting role:', err);
      alert(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Custom-role permissions are edited only via the Edit Role modal; the
  // Roles & Permissions panel is view-only, so there is no inline toggle/save.

  // Notification Settings Handlers
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email address is required';
    }
    if (!EMAIL_REGEX.test(email)) {
      return 'Please enter a valid email address';
    }
    if (notificationEmails.some(e => e.toLowerCase() === email.toLowerCase())) {
      return 'This email is already in the list';
    }
    return '';
  };

  const handleAddEmail = async () => {
    const error = validateEmail(newEmail);
    if (error) {
      setEmailError(error);
      return;
    }

    try {
      setLoadingNotifications(true);
      setEmailError('');
      const result = await workspaceService.addNotificationEmail(newEmail.trim());
      setNotificationEmails(result.notification_emails || []);
      setNewEmail('');
    } catch (err) {
      console.error('Error adding email:', err);
      setEmailError(err.message || 'Failed to add email');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    try {
      setLoadingNotifications(true);
      const result = await workspaceService.removeNotificationEmail(email);
      setNotificationEmails(result.notification_emails || []);
    } catch (err) {
      console.error('Error removing email:', err);
      alert(err.message || 'Failed to remove email');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleVulnPrefChange = (field, value) => {
    setVulnPrefs(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveVulnPrefs = async () => {
    const hours = vulnPrefs.digest_interval_hours;
    if (!Number.isInteger(hours) || hours < 1 || hours > 8760) {
      setVulnPrefsError('Digest frequency must be between 1 hour and 365 days');
      return;
    }
    setVulnPrefsError('');
    try {
      setSavingVulnPrefs(true);
      const result = await VulnerabilityManagementService.updateNotificationPreferences(vulnPrefs);
      setSavedVulnPrefs(result || vulnPrefs);
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      setVulnPrefsError(err.message || 'Failed to save notification preferences');
    } finally {
      setSavingVulnPrefs(false);
    }
  };

  const handleDiscardVulnPrefs = () => {
    setVulnPrefs({ ...savedVulnPrefs });
    setVulnPrefsError('');
  };

  // Render section content
  const renderSectionContent = () => {
    if (loading) {
      return <LoadingState title="Loading workspace data…" />;
    }

    if (error) {
      return (
        <div className={styles.errorContainer}>
          <h2>⚠️ Error Loading Data</h2>
          <p>{error}</p>
          <Button onClick={loadWorkspaceData}>
            Retry
          </Button>
        </div>
      );
    }

    switch (activeSection) {
      case 'overview':
        return <OverviewSection users={users} roles={roles} notificationEmails={notificationEmails} />;
      case 'users':
        return (
          <UserManagementSection
            users={filteredUsers}
            searchQuery={searchQuery}
            loading={loadingUsers}
            onSearchChange={handleSearchChange}
            onAddUser={handleAddUser}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onDeactivateUser={handleDeactivateUser}
          />
        );
      case 'roles':
        return (
          <RoleManagementSection
            roles={roles}
            selectedRole={selectedRole}
            permissions={permissions}
            selectedRolePermissions={selectedRolePermissions}
            onRoleSelect={handleRoleSelect}
            onAddRole={handleAddRole}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
          />
        );
      case 'notifications':
        return (
          <NotificationSection
            notificationEmails={notificationEmails}
            newEmail={newEmail}
            emailError={emailError}
            loading={loadingNotifications}
            onNewEmailChange={(e) => {
              setNewEmail(e.target.value);
              setEmailError('');
            }}
            onAddEmail={handleAddEmail}
            onRemoveEmail={handleRemoveEmail}
            onEmailKeyPress={handleEmailKeyPress}
            vulnPrefs={vulnPrefs}
            vulnPrefsChanged={vulnPrefsChanged}
            savingVulnPrefs={savingVulnPrefs}
            onVulnPrefChange={handleVulnPrefChange}
            vulnPrefsError={vulnPrefsError}
            onSaveVulnPrefs={handleSaveVulnPrefs}
            onDiscardVulnPrefs={handleDiscardVulnPrefs}
          />
        );
      default:
        return <div>Select a section</div>;
    }
  };

  return (
    <SettingsModal
      isOpen={isOpen}
      onClose={onClose}
      title="Workspace Settings"
      sections={SECTIONS}
      activeSectionId={activeSection}
      onSectionChange={setActiveSection}
    >
      {renderSectionContent()}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAddUserModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingUser ? 'Edit User' : 'Invite User'}</h2>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close"
                onClick={() => setShowAddUserModal(false)}
              >
                <Icon name="close" size={18} />
              </Button>
            </div>
            <form
              onSubmit={e => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveUser({
                  name: formData.get('name'),
                  email: formData.get('email'),
                  role_id: formData.get('role_id'),
                  status: formData.get('status')
                });
              }}
            >
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label>Name</label>
                  <Input
                    type="text"
                    name="name"
                    defaultValue={editingUser?.name}
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <Input
                    type="email"
                    name="email"
                    defaultValue={editingUser?.email}
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Role</label>
                  <select
                    name="role_id"
                    defaultValue={editingUser?.role_id || 'role_viewer'}
                    required
                  >
                    {roles.map(role => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.role_name}
                      </option>
                    ))}
                  </select>
                </div>
                {editingUser && (
                  <div className={styles.formGroup}>
                    <label>Status</label>
                    <select
                      name="status"
                      defaultValue={editingUser?.status}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending_activation">Pending Activation</option>
                    </select>
                  </div>
                )}
              </div>
              <div className={styles.modalFooter}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddUserModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={loadingUsers}
                >
                  {editingUser ? 'Update User' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Role Modal */}
      {showAddRoleModal && (() => {
        const roleFields = [
          {
            name: 'role_name',
            type: 'text',
            label: 'Role Name',
            required: true,
            maxLength: 50,
            autoFocus: true,
          },
          {
            name: 'description',
            type: 'textarea',
            label: 'Description',
            required: true,
            maxLength: 200,
            rows: 3,
          },
          ...permissions.map((category) => ({
            name: `${PERM_FIELD_PREFIX}${category.category}`,
            type: 'checkbox-group',
            label: category.category,
            options: category.permissions.map((p) => ({
              value: p.permission_id,
              label: p.permission_name,
            })),
          })),
        ];

        const roleInitial = {
          role_name: editingRole?.role_name ?? '',
          description: editingRole?.description ?? '',
          ...Object.fromEntries(
            permissions.map((category) => [
              `${PERM_FIELD_PREFIX}${category.category}`,
              category.permissions
                .filter((p) => editingRole?.permissions?.includes(p.permission_id))
                .map((p) => p.permission_id),
            ])
          ),
        };

        return (
          <FormModal
            key={editingRole?.role_id || 'new-role'}
            title={editingRole ? 'Edit Role' : 'Create Custom Role'}
            fields={roleFields}
            initialValues={roleInitial}
            submitLabel={editingRole ? 'Update Role' : 'Create Role'}
            submittingLabel="Saving..."
            maxWidth={680}
            onClose={closeRoleModal}
            onSubmit={(values) => {
              const permissionIds = permissions.flatMap(
                (category) => values[`${PERM_FIELD_PREFIX}${category.category}`] || []
              );
              return handleSaveRole({
                role_name: values.role_name,
                description: values.description,
                permissions: permissionIds,
              });
            }}
          />
        );
      })()}

      {confirmDialog}
    </SettingsModal>
  );
};

// Overview Section Component
function OverviewSection({ users, roles, notificationEmails }) {
  return (
    <div className={styles.overview}>
      <h2>Workspace Overview</h2>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{users.length}</div>
          <div className={styles.statLabel}>Total Users</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {users.filter(u => u.status === 'active').length}
          </div>
          <div className={styles.statLabel}>Active Users</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>{roles.length}</div>
          <div className={styles.statLabel}>Roles Defined</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>{notificationEmails.length}</div>
          <div className={styles.statLabel}>Notification Recipients</div>
        </div>
      </div>

      <InstructionPanel title="Quick Guide">
        <ul>
          <li><strong>User Management:</strong> Add, edit, and manage workspace members and their access levels</li>
          <li><strong>Roles & Permissions:</strong> Define custom roles and configure granular permissions</li>
          <li><strong>Notifications:</strong> Configure email recipients for threat analysis updates</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}

// User Management Section Component
function UserManagementSection({
  users,
  searchQuery,
  loading,
  onSearchChange,
  onAddUser,
  onEditUser,
  onDeleteUser,
  onDeactivateUser
}) {
  return (
    <>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>User Management</h2>
          <p className={styles.sectionDescription}>
            Manage workspace members, invite new users, and control access levels
          </p>
        </div>
      </div>

      <div className={styles.actionBar}>
        <InputGroup className={styles.searchWrapper}>
          <InputGroupInput
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={onSearchChange}
          />
          <InputGroupAddon align="inline-start">
            <Icon name="search" size={16} />
          </InputGroupAddon>
        </InputGroup>
        <Button onClick={onAddUser} startIcon={<Icon name="add" size={16} />}>
          Add User
        </Button>
      </div>

      <div className={styles.userTable}>
        <div className={styles.tableHeader}>
          <div className={styles.headerCell}>Name</div>
          <div className={styles.headerCell}>Email</div>
          <div className={styles.headerCell}>Role</div>
          <div className={styles.headerCell}>Status</div>
          <div className={styles.headerCell}>Last Active</div>
          <div className={styles.headerCell}>Actions</div>
        </div>

        <div className={styles.tableBody}>
          {loading ? (
            <LoadingState title="Loading users…" />
          ) : users.length > 0 ? (
            users.map(user => (
              <div key={user.user_id} className={styles.tableRow}>
                <div className={styles.tableCell}>
                  <div className={styles.userInfo}>
                    <Avatar size="sm">
                      <AvatarFallback>{user.name?.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span>{user.name}</span>
                  </div>
                </div>
                <div className={styles.tableCell}>{user.email}</div>
                <div className={styles.tableCell}>{user.role_name || 'Viewer'}</div>
                <div className={styles.tableCell}>
                  {(() => {
                    const preset = userStatusPreset(user.status);
                    return <StatusPill tone={preset.tone} label={preset.label} size="sm" />;
                  })()}
                </div>
                <div className={styles.tableCell}>
                  {user.last_active ? new Date(user.last_active).toLocaleDateString() : 'Never'}
                </div>
                <div className={`${styles.tableCell} ${styles.rowActions}`}>
                  <Button variant="outline" size="xs" onClick={() => onEditUser(user)}>
                    Edit
                  </Button>
                  {user.status === 'active' ? (
                    <Button variant="outline" size="xs" onClick={() => onDeactivateUser(user.user_id)}>
                      Deactivate
                    </Button>
                  ) : (
                    <Button variant="destructive" size="xs" onClick={() => onDeleteUser(user.user_id)}>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <Icon name="users" size={64} strokeWidth={1.5} />
              <h3>No Users Found</h3>
              <p>{searchQuery ? 'No users match your search' : 'Start by adding users to your workspace'}</p>
              {!searchQuery && (
                <Button onClick={onAddUser} startIcon={<Icon name="add" size={16} />}>
                  Add Your First User
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Role Management Section Component
function RoleManagementSection({
  roles,
  selectedRole,
  permissions,
  selectedRolePermissions,
  onRoleSelect,
  onAddRole,
  onEditRole,
  onDeleteRole,
}) {
  return (
    <>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Role & Permission Management</h2>
          <p className={styles.sectionDescription}>
            Define custom roles and configure permissions for fine-grained access control
          </p>
        </div>
        <Button onClick={onAddRole} startIcon={<Icon name="add" size={16} />}>
          Add Custom Role
        </Button>
      </div>

      <div className={styles.rolesContainer}>
        <div className={styles.rolesList}>
          <h3 className={styles.rolesListTitle}>Available Roles</h3>
          <div className={styles.rolesScrollArea}>
            {roles.map(role => (
              <div
                key={role.role_id}
                className={`${styles.roleItem} ${selectedRole?.role_id === role.role_id ? styles.roleItemActive : ''}`}
                onClick={() => onRoleSelect(role)}
              >
                <div className={styles.roleItemContent}>
                  <div className={styles.roleItemHeader}>
                    <span className={styles.roleName}>{role.role_name}</span>
                    {role.is_system_role && (
                      <span className={styles.systemBadge}>System</span>
                    )}
                  </div>
                  <span className={styles.roleDescription}>
                    {role.is_system_role ? role.description : `${role.user_count} users`}
                  </span>
                </div>
                {!role.is_system_role && (
                  <div className={styles.roleActions}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Edit Role"
                      aria-label="Edit Role"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditRole(role);
                      }}
                    >
                      <Icon name="edit" size={16} />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon-sm"
                      title="Delete Role"
                      aria-label="Delete Role"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteRole(role.role_id);
                      }}
                    >
                      <Icon name="delete" size={16} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.permissionsPanel}>
          <div className={styles.permissionsPanelHeader}>
            <h3>{selectedRole?.role_name || 'Select a Role'}</h3>
            {selectedRole && (
              <p className={styles.permissionsPanelDescription}>
                {selectedRole.is_system_role
                  ? 'System roles have predefined permissions that cannot be modified'
                  : 'Use the Edit button on this role to modify its permissions'}
              </p>
            )}
          </div>

          {selectedRole ? (
            <div className={styles.permissionsGrid}>
              {permissions.map(category => (
                <div key={category.category} className={styles.permissionCategory}>
                  <h4 className={styles.permissionCategoryTitle}>{category.category}</h4>
                  <div className={styles.permissionItems}>
                    {category.permissions.map(permission => (
                      <div key={permission.permission_id} className={styles.permissionItem}>
                        <Checkbox
                          className={styles.permissionCheckbox}
                          checked={selectedRolePermissions.includes(permission.permission_id) || selectedRolePermissions.includes('*')}
                          disabled
                        />
                        <div className={styles.permissionInfo}>
                          <span className={styles.permissionName}>{permission.permission_name}</span>
                          <span className={styles.permissionDescription}>{permission.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Icon name="star" size={64} strokeWidth={1.5} />
              <h3>No Role Selected</h3>
              <p>Select a role from the list to view its permissions</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Notification Section Component
function NotificationSection({
  notificationEmails,
  newEmail,
  emailError,
  loading,
  onNewEmailChange,
  onAddEmail,
  onRemoveEmail,
  onEmailKeyPress,
  vulnPrefs,
  vulnPrefsChanged,
  savingVulnPrefs,
  vulnPrefsError,
  onVulnPrefChange,
  onSaveVulnPrefs,
  onDiscardVulnPrefs
}) {
  const isDays = vulnPrefs.digest_interval_hours >= 24;
  const derivedValue = isDays
    ? Math.round(vulnPrefs.digest_interval_hours / 24)
    : vulnPrefs.digest_interval_hours;
  const [digestInputValue, setDigestInputValue] = useState(String(derivedValue));

  useEffect(() => {
    const days = vulnPrefs.digest_interval_hours >= 24;
    const val = days
      ? Math.round(vulnPrefs.digest_interval_hours / 24)
      : vulnPrefs.digest_interval_hours;
    setDigestInputValue(String(val));
  }, [vulnPrefs.digest_interval_hours]);

  return (
    <>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Notification settings</h2>
          <p className={styles.sectionDescription}>
            Configure email addresses to receive threat analysis update notifications
          </p>
        </div>
      </div>

      <div className={styles.notificationContent}>
        <div className={styles.emailInputSection}>
          <div className={styles.emailInputWrapper}>
            <Input
              type="email"
              className={styles.emailInput}
              aria-invalid={emailError ? true : undefined}
              placeholder="Enter email address"
              value={newEmail}
              onChange={onNewEmailChange}
              onKeyPress={onEmailKeyPress}
              disabled={loading}
            />
            <Button
              onClick={onAddEmail}
              loading={loading}
              disabled={!newEmail.trim()}
            >
              Add
            </Button>
          </div>
          {emailError && (
            <span className={styles.errorText}>{emailError}</span>
          )}
        </div>

        <div className={styles.emailListSection}>
          <h3 className={styles.emailListTitle}>
            Notification recipients ({notificationEmails.length})
          </h3>

          {notificationEmails.length > 0 ? (
            <div className={styles.emailList}>
              {notificationEmails.map((email, index) => (
                <div key={index} className={styles.emailItem}>
                  <div className={styles.emailInfo}>
                    <Icon name="mail" size={18} />
                    <span>{email}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onRemoveEmail(email)}
                    disabled={loading}
                    title="Remove email"
                    aria-label="Remove email"
                  >
                    <Icon name="close" size={16} />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyEmailState}>
              <Icon name="notification" size={48} strokeWidth={1.5} />
              <p>No notification emails configured</p>
              <span>Add email addresses above to receive threat analysis notifications</span>
            </div>
          )}
        </div>

        <div className={styles.vulnPrefsSection}>
          <h3 className={styles.emailListTitle}>Vulnerability Management alerts</h3>

          <div className={styles.prefRow}>
            <div className={styles.prefInfo}>
              <span className={styles.prefLabel}>Realtime alerts</span>
              <span className={styles.prefDescription}>
                Notify recipients immediately when a new CVE is detected in your projects
              </span>
            </div>
            <Switch
              checked={vulnPrefs.realtime_enabled}
              onCheckedChange={(next) => onVulnPrefChange('realtime_enabled', next)}
            />
          </div>

          <div className={styles.prefRow}>
            <div className={styles.prefInfo}>
              <span className={styles.prefLabel}>Enable vulnerability digest</span>
              <span className={styles.prefDescription}>
                Send a periodic email summarizing all new CVEs found across your projects
              </span>
            </div>
            <Switch
              checked={vulnPrefs.digest_enabled}
              onCheckedChange={(next) => onVulnPrefChange('digest_enabled', next)}
            />
          </div>

          {vulnPrefs.digest_enabled && (
            <div className={styles.prefRow}>
              <div className={styles.prefInfo}>
                <span className={styles.prefLabel}>Digest frequency</span>
                <span className={styles.prefDescription}>
                  Set how often the digest email is delivered to recipients
                </span>
              </div>
              <div className={styles.digestFrequencyControls}>
                <Input
                  type="number"
                  className={styles.digestFrequencyInput}
                  value={digestInputValue}
                  min={1}
                  onChange={(e) => {
                    setDigestInputValue(e.target.value);
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 1) {
                      onVulnPrefChange('digest_interval_hours', isDays ? val * 24 : val);
                    }
                  }}
                />
                <select
                  className={styles.prefSelect}
                  value={isDays ? 'days' : 'hours'}
                  onChange={(e) => {
                    const displayVal = parseInt(digestInputValue) || 1;
                    if (e.target.value === 'days') {
                      onVulnPrefChange('digest_interval_hours', displayVal * 24);
                    } else {
                      onVulnPrefChange('digest_interval_hours', displayVal);
                    }
                  }}
                >
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          )}

          <div className={styles.prefRow}>
            <div className={styles.prefInfo}>
              <span className={styles.prefLabel}>Tracked versions only</span>
              <span className={styles.prefDescription}>
                Only include vulnerabilities from versions marked as tracked
              </span>
            </div>
            <Switch
              checked={vulnPrefs.active_only}
              onCheckedChange={(next) => onVulnPrefChange('active_only', next)}
            />
          </div>

          {vulnPrefsChanged && (
            <div className={styles.vulnPrefsActions}>
              <Button onClick={onSaveVulnPrefs} loading={savingVulnPrefs}>
                Save changes
              </Button>
              <Button
                variant="outline"
                onClick={onDiscardVulnPrefs}
                disabled={savingVulnPrefs}
              >
                Discard changes
              </Button>
            </div>
          )}
          {vulnPrefsError && (
            <span className={styles.errorText}>{vulnPrefsError}</span>
          )}
        </div>

        <InstructionPanel title="Notification Information">
          <ul>
            <li>Email notifications are sent when new threats or vulnerabilities applicable to your workspace are detected.</li>
            <li>Threat notifications are triggered when new entries in our threat database impact your TARA analyses.</li>
            <li>Vulnerability notifications are triggered when new CVEs are found in your Vulnerability Management projects.</li>
          </ul>
        </InstructionPanel>
      </div>
    </>
  );
}

export default WorkspaceSettingsModal;
